import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, Dimensions, Platform, SafeAreaView } from 'react-native';
import { Ionicons, FontAwesome5, MaterialCommunityIcons } from '@expo/vector-icons';
import { navigate } from '../../../../../navigation/RootNavigation';
import { StatusBar } from 'expo-status-bar';

const BRAND = {
  primary: '#6D08C0',
  primaryDeep: '#4E0D91',
  secondary: '#365FF2',
  soft: '#F6EEFF',
  border: '#E8D9FF',
  white: '#FFFFFF',
  text: '#344054',
};

const TABS = [
  {
    label: 'HOME',
    iconName: 'home-outline',
    iconFamily: 'ion',
    routeKey: 'Tenants',
  },
  {
    label: 'Current',
    iconName: 'user-circle',
    iconFamily: 'fa5',
    routeKey: 'CurrentOnboards',
  },
  {
    label: 'Live',
    iconName: 'user-check',
    iconFamily: 'fa5',
    routeKey: 'CompletedOnboards',
  },
  {
    label: 'Renewals',
    iconName: 'autorenew',
    iconFamily: 'mci',
    routeKey: 'RenewalsFixedTerm',
  },
  {
    label: 'Archived',
    iconName: 'archive',
    iconFamily: 'fa5',
    routeKey: 'ArchivedOnboards',
  },
];

const renderIcon = (tab, isSelected) => {
  const color = isSelected ? BRAND.white : BRAND.primary;
  const size = tab.label === 'Renewals' ? 20 : 18;

  if (tab.iconFamily === 'ion') {
    return <Ionicons name={tab.iconName} size={20} color={color} />;
  }

  if (tab.iconFamily === 'mci') {
    return <MaterialCommunityIcons name={tab.iconName} size={size} color={color} />;
  }

  return <FontAwesome5 name={tab.iconName} size={16} color={color} />;
};

const HorizontalTabSliderWelcome = (props) => {
  const [selectedTab, setSelectedTab] = useState('Tenants');
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  const scrollViewRef = useRef(null);

  const isCompact = screenWidth < 768;
  const isSmallScreen = screenWidth < 420;

  useEffect(() => {
    const updateLayout = () => {
      setScreenWidth(Dimensions.get('window').width);
    };

    const dimensionsListener = Dimensions.addEventListener('change', updateLayout);

    return () => {
      if (dimensionsListener?.remove) {
        dimensionsListener.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (props?.props?.route?.name) {
      setSelectedTab(props.props.route.name);
    } else {
      setSelectedTab('Tenants');
    }
  }, [props?.props?.route?.name, props?.props?.props?.route?.name]);

  useEffect(() => {
    if (isCompact || !scrollViewRef.current) {
      return;
    }

    const selectedIndex = TABS.findIndex((tab) => tab.routeKey === selectedTab);
    if (selectedIndex === -1) {
      return;
    }

    const tabWidth = isCompact ? (isSmallScreen ? 112 : 126) : 148;
    const gap = 12;
    const targetX = Math.max(0, selectedIndex * (tabWidth + gap) - containerWidth / 2 + tabWidth / 2);

    scrollViewRef.current.scrollTo({ x: targetX, animated: true });
  }, [selectedTab, containerWidth, isCompact, isSmallScreen]);

  const scrollHorizontal = (direction) => {
    if (!scrollViewRef.current) {
      return;
    }

    const step = Math.max(containerWidth * 0.72, 180);
    const newX =
      direction === 'right'
        ? Math.min(scrollX + step, Math.max(contentWidth - containerWidth, 0))
        : Math.max(scrollX - step, 0);

    scrollViewRef.current.scrollTo({
      x: newX,
      animated: true,
    });

    setScrollX(newX);
  };

  const canScrollLeft = scrollX > 1;
  const canScrollRight = scrollX < contentWidth - containerWidth - 1;

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar style="auto" />
      <View style={styles.outerShell}>
        <View style={styles.topGlow} />
        <View style={styles.wrapper}>
          <View style={styles.headerRow}>
            <View>
              <Text style={styles.eyebrow}>Tenant hub</Text>
              <Text style={styles.heading}>OpenFlo workspace</Text>
            </View>

          </View>

          {isCompact ? (
            <View style={styles.mobileTabList}>
              {TABS.map((tab, index) => {
                const isSelected = tab.routeKey === selectedTab;
                return (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.tab,
                      styles.mobileVerticalTab,
                      isSmallScreen && styles.smallScreenVerticalTab,
                      isSelected && styles.selectedTab,
                    ]}
                    onPress={() => navigate(tab.routeKey)}
                    activeOpacity={0.85}
                  >
                    <View style={[styles.iconWrap, styles.mobileIconWrap, isSelected && styles.selectedIconWrap]}>
                      {renderIcon(tab, isSelected)}
                    </View>
                    <Text style={[styles.label, styles.compactLabel, isSelected && styles.selectedLabel]} numberOfLines={1}>
                      {tab.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : (
            <View style={styles.scrollContainer}>
              <TouchableOpacity
                style={[styles.arrowButton, styles.leftArrow, !canScrollLeft && styles.arrowButtonDisabled]}
                onPress={() => scrollHorizontal('left')}
                disabled={!canScrollLeft}
              >
                <Ionicons name="chevron-back" size={18} color={BRAND.white} />
              </TouchableOpacity>

              <ScrollView
                ref={scrollViewRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.container}
                contentContainerStyle={styles.contentContainer}
                scrollEventThrottle={16}
                onContentSizeChange={(w) => setContentWidth(w)}
                onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
                onScroll={(e) => setScrollX(e.nativeEvent.contentOffset.x)}
              >
                {TABS.map((tab, index) => {
                  const isSelected = tab.routeKey === selectedTab;
                  return (
                    <TouchableOpacity
                      key={index}
                      style={[styles.tab, styles.desktopTab, isSelected && styles.selectedTab]}
                      onPress={() => navigate(tab.routeKey)}
                      activeOpacity={0.85}
                    >
                      <View style={[styles.iconWrap, isSelected && styles.selectedIconWrap]}>
                        {renderIcon(tab, isSelected)}
                      </View>
                      <Text style={[styles.label, isSelected && styles.selectedLabel]} numberOfLines={1}>
                        {tab.label}
                      </Text>
                      {isSelected ? <View style={styles.selectedUnderline} /> : null}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>

              <TouchableOpacity
                style={[styles.arrowButton, styles.rightArrow, !canScrollRight && styles.arrowButtonDisabled]}
                onPress={() => scrollHorizontal('right')}
                disabled={!canScrollRight}
              >
                <Ionicons name="chevron-forward" size={18} color={BRAND.white} />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: '#F8F7FD',
  },
  outerShell: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 8,
    backgroundColor: '#F8F7FD',
  },
  topGlow: {
    position: 'absolute',
    top: 10,
    left: 28,
    right: 28,
    height: 130,
    borderRadius: 32,
    backgroundColor: '#F3E8FF',
    opacity: 0.8,
  },
  wrapper: {
    backgroundColor: 'rgba(255,255,255,0.88)',
    borderWidth: 1,
    borderColor: BRAND.border,
    borderRadius: 30,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 16,
    shadowColor: '#39135F',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 5,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: BRAND.secondary,
    marginBottom: 4,
  },
  heading: {
    fontSize: 22,
    fontWeight: '800',
    color: BRAND.primaryDeep,
  },
  helperPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BRAND.soft,
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
  },
  helperText: {
    marginLeft: 6,
    color: BRAND.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  mobileTabList: {
    width: '100%',
  },
  scrollContainer: {
    position: 'relative',
    justifyContent: 'center',
  },
  container: {
    overflow: 'visible',
  },
  contentContainer: {
    paddingHorizontal: 2,
    paddingVertical: 4,
    paddingRight: 22,
  },
  tab: {
    marginRight: 12,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E9E4F5',
    backgroundColor: BRAND.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#1F1440',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.06,
    shadowRadius: 18,
    elevation: 3,
  },
  compactTab: {
    width: 104,
    minHeight: 94,
    paddingHorizontal: 10,
    paddingVertical: 12,
  },
  mobileVerticalTab: {
    width: '100%',
    minHeight: 86,
    marginRight: 0,
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 20,
  },
  desktopTab: {
    width: 148,
    minHeight: 118,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  smallScreenTab: {
    width: 96,
    minHeight: 88,
    paddingHorizontal: 8,
  },
  smallScreenVerticalTab: {
    minHeight: 82,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  selectedTab: {
    backgroundColor: BRAND.primary,
    borderColor: BRAND.primary,
    transform: [{ translateY: -2 }],
  },
  iconWrap: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: BRAND.soft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  mobileIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 14,
    marginBottom: 0,
    marginRight: 12,
  },
  selectedIconWrap: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  label: {
    fontSize: 15,
    fontWeight: '800',
    color: BRAND.text,
    textAlign: 'center',
  },
  selectedLabel: {
    color: BRAND.white,
  },
  compactLabel: {
    fontSize: 12,
    textAlign: 'left',
    flex: 1,
  },
  selectedUnderline: {
    width: 28,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.82)',
    marginTop: 10,
  },
  arrowButton: {
    position: 'absolute',
    zIndex: 10,
    top: '50%',
    marginTop: -18,
    backgroundColor: BRAND.primary,
    borderRadius: 18,
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#39135F',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.16,
        shadowRadius: 12,
      },
      android: {
        elevation: 5,
      },
    }),
  },
  arrowButtonDisabled: {
    opacity: 0.25,
  },
  leftArrow: {
    left: -6,
  },
  rightArrow: {
    right: -6,
  },
});

export default HorizontalTabSliderWelcome;
